import ReactGA4 from 'react-ga4'
import { Parallax } from 'react-scroll-parallax'
import type { Heading } from '../types/hero.d'

interface HeroProps {
  heading: Heading
  subheading: string[]
  href: string
}

export default function Hero({ heading, subheading, href }: HeroProps) {
  return (
    <section
      id="hero"
      className="w-full"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => { ReactGA4.event({ category: 'Hero', action: 'Click', label: href })}}
        className="group block relative"
        aria-label={`Visit ${heading.heading === "Recording & rehearsal studio" ? "Strangeways Studios recording studio" : "Strangeways.dev software development"} website`}
      >
        <div className="fit-content relative h-[400px] overflow-hidden rounded-2xl transition-all duration-700 ease-out lg:h-[500px] group-hover:shadow-[0_25px_50px_-12px] group-hover:shadow-brand-500/25 dark:group-hover:shadow-brand-400/15 group-focus-visible:ring-2 group-focus-visible:ring-brand-500 group-focus-visible:ring-offset-4 dark:group-focus-visible:ring-offset-neutral-900 bg-linear-to-br from-white/10 to-brand-950/5 backdrop-blur-sm border border-white/20 dark:border-neutral-800/50">
          {/* Enhanced animated background pattern */}
          <div className="absolute inset-0 opacity-40 dark:opacity-30">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,brand-500_1px,transparent_1px)] bg-size-[24px_24px] animate-pulse"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_2px_2px,brand-400_0.5px,transparent_0.5px)] bg-size-[48px_48px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
          
          {/* Floating brand elements */}
          <div className="absolute top-10 left-10 w-20 h-20 bg-brand-500/10 rounded-full blur-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
          <div className="absolute bottom-20 right-20 w-16 h-16 bg-brand-400/10 rounded-full blur-lg animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
          
          {/* Parallaxed image */}
          <Parallax
            translateY={[-8, 8]}
            disabled={false}
            className="absolute inset-0 z-0 transition-transform duration-1000 ease-out group-hover:scale-110"
          >
            <picture>
              <source
                type="image/webp"
                srcSet={`/media/hero/${heading.image ?? 'strangeways_2025'}_320.webp 320w, 
                /media/hero/${heading.image ?? 'strangeways_2025'}_480.webp 480w, 
                /media/hero/${heading.image ?? 'strangeways_2025'}_640.webp 640w, 
                /media/hero/${heading.image ?? 'strangeways_2025'}_768.webp 768w, 
                /media/hero/${heading.image ?? 'strangeways_2025'}_960.webp 960w, 
                /media/hero/${heading.image ?? 'strangeways_2025'}_1200.webp 1200w`}
                sizes="100vw"
              />
              <img
                src={`/media/hero/${heading.image ?? 'strangeways_2025'}_768.webp`}
                alt={heading.heading === "Recording & rehearsal studio" 
                  ? "Strangeways recording studio interior with professional audio equipment and instruments" 
                  : "Software development workspace with modern computers and development setup"}
                className="h-full w-full scale-100 object-cover object-[center_60%]"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </Parallax>

          {/* Enhanced gradient overlay for better text readability */}
          <div
            className="bg-linear-to-br pointer-events-none absolute inset-0 z-10 from-transparent via-brand-950/40 to-brand-950/80 transition-all duration-700 group-hover:via-brand-950/50 group-hover:to-brand-950/90 dark:from-brand-50/20 dark:via-brand-900/60 dark:to-brand-900/95"
            aria-hidden="true"
          />

          {/* Content */}
          <div className="relative z-20 flex h-full items-end justify-between px-6 pb-6 sm:pb-10 sm:px-8">
            <div className="space-y-4 sm:space-y-6 max-w-3xl">
              {heading.heading && (
                <h2 className="lg:text-5xl max-w-xl text-3xl font-bold tracking-tight text-white drop-shadow-2xl sm:text-4xl transition-all duration-500 group-hover:scale-105 animate-fade-in-up">
                  {heading.heading}
                </h2>
              )}
              {heading.paragraph && (
                <p className="text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed text-white drop-shadow-xl transition-all duration-500 group-hover:text-white animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  {heading.paragraph}
                </p>
              )}
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm font-medium text-white drop-shadow-lg" style={{ animationDelay: '0.2s' }}>
                {subheading.map((sub) => (
                  <span key={`subheading-${sub.replace(/\s+/g, '-').toLowerCase()}`} className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 transition-all duration-300 group-hover:bg-white/30 group-hover:border-white/40 hover:scale-105 hover:shadow-lg">
                    {sub}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Enhanced CTA indicator */}
            <div className="hidden sm:flex flex-col items-center gap-3 text-white opacity-0 translate-x-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 animate-fade-in-right" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 hover:bg-white/30 hover:scale-105 transition-all duration-300">
                <span className="text-sm font-medium">Visit</span>
                <svg 
                  className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
              <div className="w-0.5 h-8 bg-white/30"></div>
            </div>
          </div>
        </div>
      </a>
    </section>
  )
}
